import React, { useEffect, useState } from "react";
import { Menu, Phone, X, ArrowRight } from "lucide-react";
import { PHONE, PHONE_TEL, LOGO_URL } from "../lib/utils";
import PreReservationDialog from "./PreReservationDialog";

const NAV = [
  { href: "#terrains", label: "Nos terrains" },
  { href: "#fougeres", label: "Fougères" },
  { href: "#louvigne", label: "Louvigné-du-Désert" },
  { href: "#tarifs", label: "Tarifs" },
  { href: "#evenements", label: "Événements" },
  { href: "#avis", label: "Avis" },
  { href: "#acces", label: "Accès" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl border-b border-gray-200"
          : "bg-white/60 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-20 md:h-24 flex items-center justify-between gap-4">
        {/* Logo */}
        <a
          href="#top"
          data-testid="header-logo-link"
          className="flex items-center gap-2 md:gap-3 shrink-0 min-w-0"
        >
          <img
            src={LOGO_URL}
            alt="Logo Paintball Aventures 35 — paintball Fougères Louvigné-du-Désert Ille-et-Vilaine"
            className="h-12 w-12 md:h-16 md:w-16 object-contain shrink-0 drop-shadow-[0_2px_4px_rgba(0,0,0,0.25)]"
            loading="eager"
          />
          <div className="leading-tight hidden sm:block">
            <p className="font-display text-sm md:text-lg font-bold uppercase tracking-tight">
              Paintball Aventures
            </p>
            <p className="font-mono text-[9px] md:text-xs text-[#E91E63] font-bold tracking-[0.18em]">
              FOUGÈRES · LOUVIGNÉ-DU-DÉSERT
            </p>
          </div>
        </a>

        {/* Desktop nav */}
        <nav
          aria-label="Navigation principale"
          className="hidden xl:flex items-center gap-7"
        >
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-${n.href.replace("#", "")}`}
              className="font-body text-sm font-semibold text-gray-700 hover:text-[#E91E63] transition-colors whitespace-nowrap"
            >
              {n.label}
            </a>
          ))}
        </nav>

        {/* CTA cluster */}
        <div className="flex items-center gap-2 md:gap-3">
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="header-call-button"
            className="hidden sm:inline-flex items-center gap-2 px-3 md:px-4 py-2.5 border-2 border-black text-black font-bold uppercase text-xs md:text-sm tracking-wider hover:bg-black hover:text-white transition-colors whitespace-nowrap"
          >
            <Phone className="w-4 h-4" strokeWidth={2.5} />
            <span className="hidden md:inline">{PHONE}</span>
            <span className="md:hidden">Appeler</span>
          </a>
          <PreReservationDialog
            trigger={
              <button
                data-testid="header-reserve-button"
                className="inline-flex items-center gap-2 px-3 md:px-5 py-2.5 bg-[#E91E63] text-white font-bold uppercase text-xs md:text-sm tracking-wider hover:-translate-y-0.5 hover:shadow-[0_8px_0_-2px_#0a0a0a] transition-all whitespace-nowrap"
              >
                <span className="hidden sm:inline">Pré-réservation</span>
                <span className="sm:hidden">Réserver</span>
                <ArrowRight className="w-3.5 h-3.5 hidden sm:inline" strokeWidth={2.5} />
              </button>
            }
          />
          <button
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
            className="xl:hidden p-2 -mr-2"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="xl:hidden bg-white border-t border-gray-200">
          <nav className="px-4 py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.href.replace("#", "")}`}
                className="py-3 font-body font-semibold text-gray-800 border-b border-gray-100 hover:text-[#E91E63]"
              >
                {n.label}
              </a>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
